import type { LicensesFilter } from "@/model/LicensesFilter";
import { defineStore } from "pinia";

const userSessionIdHashKey = "dashboard.session-id-hash";
const licensesFilterKey = "dashboard.licenses-filter";

interface StorageItem<T> {
    sessionIdHash: string;
    value: T;
}

export enum IntroType {
    UsageHistoryGroupFilter
}

const introKeyMap: Map<IntroType, string> = new Map([
    [IntroType.UsageHistoryGroupFilter, "dashboard.usage-history.group-filter-intro"]
]);

export const useUserPreferencesStore = defineStore("userPreferencesStore", () => {
    function getSessionIdHash(): string | undefined {
        return localStorage.getItem(userSessionIdHashKey) ?? undefined;
    }

    function setSessionIdHash(value?: string) {
        if (value != null) {
            localStorage.setItem(userSessionIdHashKey, value);
        } else {
            localStorage.removeItem(userSessionIdHashKey);
        }
    }

    function getLicensesFilter(): LicensesFilter | undefined {
        const storageItemAsText = localStorage.getItem(licensesFilterKey);
        if (storageItemAsText == null) {
            return undefined;
        }
        const storageItem = JSON.parse(storageItemAsText) as StorageItem<LicensesFilter>;
        const sessionIdHash = getSessionIdHash();

        if (storageItem.sessionIdHash == sessionIdHash) {
            return storageItem.value;
        }

        return {
            activeOnly: true
        };
    }

    function setLicensesFilter(filter: LicensesFilter) {
        const sessionIdHash = getSessionIdHash();
        const storageItem: StorageItem<LicensesFilter> = {
            sessionIdHash: sessionIdHash ?? "",
            value: filter
        };
        localStorage.setItem(licensesFilterKey, JSON.stringify(storageItem));
    }

    function getIntroConfirmed(type: IntroType) {
        const introKeyName = introKeyMap.get(type);
        if (introKeyName == null)
            return false;
        return localStorage.getItem(introKeyName) != null;
    }

    function setIntroConfirmed(type: IntroType, value: boolean) {
        const introKeyName = introKeyMap.get(type);
        if (introKeyName == null)
            return;

        if (value)
            localStorage.setItem(introKeyName, "confirmed");
        else
            localStorage.removeItem(introKeyName);
    }

    return {
        getSessionIdHash, setSessionIdHash,
        getLicensesFilter, setLicensesFilter,
        getIntroConfirmed, setIntroConfirmed
    };
});
